import { useMemo } from "react"
import { observer } from "mobx-react-lite"
import { Address } from "viem"
import { useConfig } from "wagmi"

import { useScaleApplication } from "@/domains/application/use-scale-application"
import { claimAllNftBribesInteraction } from "@/domains/interactions/claim-all-nft-bribes/claim-all-nft-bribes-interaction"
import { cn } from "@/domains/library"
import { BigTokenGroupCard } from "@/domains/lock-rewards/components/big-token-group-card"
import { LockRewardsPageHeader } from "@/domains/lock-rewards/components/page-header"
import { GroupVotingRewardsBy } from "@/domains/old-voting-rewards/components/voting-rewards-tabs"
import { useChainConfig } from "@/hooks/use-chain-config"
import useEventEmitter from "@/hooks/useEventEmitter"

import { ClaimEverythingByNftFormat } from "../strategies/selection/lock-reward-selection-strategy"

export const LockRewardsGroupedByToken = observer(
  ({ groupBy, setGroupBy }: { groupBy: GroupVotingRewardsBy; setGroupBy: (groupBy: GroupVotingRewardsBy) => void }) => {
    const scaleApp = useScaleApplication()
    const config = useConfig()
    const emitter = useEventEmitter()
    const chainConfig = useChainConfig()
    const groupStrategy = scaleApp.LockRewards.Strategies.TokenGrouping
    const allTokens = scaleApp.LockRewards.Repository.getAll()
    const groupedTokens = groupStrategy.group(allTokens)
    const selectionStrategy = scaleApp.LockRewards.Strategies.TokenSelection
    const claimFormat = useMemo(() => selectionStrategy.toClaimFormat(allTokens), [selectionStrategy, allTokens])

    const chainId = chainConfig.chainId

    const selectedRewardsValueUSD = useMemo(() => {
      return selectionStrategy.filteredSelected(allTokens).reduce((acc, item) => acc + item.valueUSD, 0)
    }, [selectionStrategy, allTokens])

    const claimAllHandler = async () => {
      for (let i = 0; i < claimFormat.length; i++) {
        const item = claimFormat[i] as ClaimEverythingByNftFormat
        if (!item) continue

        const result = await claimAllNftBribesInteraction({
          account: scaleApp.Wallet.Connection.connectedAddress?.toViemAddress() as Address,
          chainId,
          config,
          voterAddress: chainConfig.equalizer.contracts.voter,
          bribes: item.bribes as Address[],
          bribeTokens: item.bribeTokens as Array<Array<Address>>,
          nft: { id: item.nftId },
          emitter,
        })

        if (result === false) {
          break
        }
      }
    }

    return (
      <>
        <LockRewardsPageHeader
          groupBy={groupBy}
          numberOfLocksWithRewards={claimFormat.length}
          setGroupBy={setGroupBy}
          totalRewardsValueUSD={selectedRewardsValueUSD}
          claimAllHandler={claimAllHandler}
        />
        <div className="flex flex-col gap-4">
          <div className={cn("grid max-w-6xl grid-cols-[repeat(auto-fit,minmax(158px,1fr))] gap-2")}>
            {Array.from(groupedTokens.entries()).map(([tokenAddress, tokenGroup]) => {
              return (
                <BigTokenGroupCard
                  key={tokenAddress}
                  tokenGroup={tokenGroup}
                  isSelected={selectionStrategy.has(tokenAddress)}
                  onToggleSelect={() => selectionStrategy.toggle(tokenAddress)}
                />
              )
            })}
          </div>
        </div>
      </>
    )
  },
)
